import React from 'react';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { getEst3CostProvenance } from './est3CostProvenance';
import {
  EST3_MATERIAL_COMPONENT_KINDS,
  EST3_MATERIAL_RATE_SOURCES,
  summarizeMaterialComponents,
} from './est3MaterialKinds';

const { Text, Title } = Typography;

export interface EpcEstimateMaterialBuildUpPanelProps {
  estimateId: string;
  revisionId: string;
  costLineId: string | null;
  costLine?: Record<string, unknown> | null;
  readOnly?: boolean;
  onChanged?: () => void;
  compact?: boolean;
}

/**
 * EST3 UX2 Wave 4 — Landed material build-up (components + waterfall + catalogue seed).
 */
export const EpcEstimateMaterialBuildUpPanel: React.FC<EpcEstimateMaterialBuildUpPanelProps> = ({
  estimateId,
  revisionId,
  costLineId,
  costLine,
  readOnly,
  onChanged,
  compact,
}) => {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const compQ = useQuery({
    queryKey: ['epc-est-mat', estimateId, costLineId],
    queryFn: () => epcEstimationService.listMaterialComponents(estimateId, costLineId!),
    enabled: !!estimateId && !!costLineId,
  });

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createMaterialComponent(estimateId, costLineId!, {
        revisionId,
        ...v,
      }),
    onSuccess: () => {
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-mat', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const deleteM = useMutation({
    mutationFn: (componentId: string) =>
      epcEstimationService.deleteMaterialComponent(estimateId, costLineId!, componentId, revisionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-est-mat', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  if (!costLineId) {
    return <Empty description="Select a material cost line to view landed build-up." />;
  }

  const components = compQ.data || [];
  const { byKind, landedTotal } = summarizeMaterialComponents(components);
  const lineProv = costLine ? getEst3CostProvenance(costLine) : null;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {costLine ? (
        <Descriptions size="small" column={compact ? 1 : 2} bordered>
          <Descriptions.Item label="Cost line">{String(costLine.description || '—')}</Descriptions.Item>
          <Descriptions.Item label="Line total">
            {formatMoney(costLine.total_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
          {lineProv && lineProv.summary !== '—' ? (
            <Descriptions.Item label="Line provenance" span={2}>
              {lineProv.summary}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      ) : null}

      <Card size="small" title="Landed cost waterfall">
        {byKind.length === 0 ? (
          <Text type="secondary">No components — line rate is manual until build-up is added.</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {byKind.map((row) => (
              <div
                key={row.kind}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
              >
                <Tag>{row.kind}</Tag>
                <Text>{formatMoney(row.amount, { display: 'plain', maximumFractionDigits: 2 })}</Text>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 600,
                borderTop: '1px solid #f0f0f0',
                paddingTop: 8,
              }}
            >
              <Text>Landed total</Text>
              <Text>{formatMoney(landedTotal, { display: 'plain', maximumFractionDigits: 2 })}</Text>
            </div>
          </Space>
        )}
      </Card>

      {!readOnly ? (
        <Card size="small" title="Add component">
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => createM.mutate(v)}
            initialValues={{ componentKind: 'BASE', quantity: 1, rateSource: 'CATALOGUE' }}
          >
            <Form.Item name="itemId" label="Inventory item (catalogue / last purchase)">
              <ItemSelect allowClear placeholder="Select item to seed rate" />
            </Form.Item>
            <Form.Item name="componentKind" label="Kind">
              <Select options={EST3_MATERIAL_COMPONENT_KINDS.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input />
            </Form.Item>
            <Space wrap style={{ width: '100%' }}>
              <Form.Item name="quantity" label="Qty">
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="rate" label="Rate">
                <InputNumber min={0} placeholder="Blank = catalogue" />
              </Form.Item>
              <Form.Item name="rateSource" label="Source">
                <Select
                  style={{ width: 140 }}
                  options={EST3_MATERIAL_RATE_SOURCES.map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Space>
            <Form.Item name="sourceRefLabel" label="Provenance label">
              <Input placeholder="e.g. Supplier Quote RFQ-1048" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={createM.isPending} block>
              Add component
            </Button>
          </Form>
        </Card>
      ) : null}

      <Card
        size="small"
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Components
            </Title>
            <Tag>{components.length}</Tag>
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="id"
          loading={compQ.isLoading}
          dataSource={components}
          pagination={components.length > 20 ? { pageSize: 20 } : false}
          scroll={{ x: true }}
          columns={[
            { title: 'Kind', dataIndex: 'component_kind', width: 100 },
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            { title: 'Qty', dataIndex: 'quantity', width: 70 },
            {
              title: 'Rate',
              dataIndex: 'rate',
              width: 90,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 100,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Provenance',
              width: 140,
              ellipsis: true,
              render: (_: unknown, row: any) => {
                const p = getEst3CostProvenance(row);
                return p.summary !== '—' ? (
                  <Tag color="blue">{p.summary}</Tag>
                ) : (
                  <Text type="secondary">{row.rate_source || '—'}</Text>
                );
              },
            },
            ...(!readOnly
              ? [
                  {
                    title: '',
                    width: 70,
                    render: (_: unknown, row: any) => (
                      <Popconfirm
                        title="Remove component?"
                        onConfirm={() => deleteM.mutate(row.id)}
                      >
                        <Button size="small" danger loading={deleteM.isPending}>
                          Del
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </Space>
  );
};
